import { useState } from 'react';
import { useRouter } from 'next/router';

import { Heading, Icon, List, Modal, Paragraph } from '@zillow/constellation-10';
import { IconMovingTruckFilled } from '@zillow/constellation-icons';

import {
  useRentalManagerUserFeedListingManagementV2WebApiHandler_createManualVersion,
  useRentalManagerUserPropertyFeedToManualV2WebApiHandler_convert,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { DEFAULT_ERROR_MESSAGE } from '../../../constants/ErrorMessageConstants';
import { getCreateListingRoute, getListingRoute } from '../../../constants/urls';

import ModalFooter from '../ModalFooter';

const infoList = [
  'You can make edits to the new listing with Zillow Rental Manager',
  'Publish this version of the listing and renters will see the updates right away',
  'You can switch back to your feed listing whenever you like',
];

type TransferFeedListingModalProps = {
  listingAlias: string;
  isMultifamily?: boolean;
  useCreateManualVersion?: boolean;
};

const TransferFeedListingModal = ({
  listingAlias,
  isMultifamily,
  useCreateManualVersion = false,
}: TransferFeedListingModalProps): JSX.Element => {
  const router = useRouter();
  const [errorMessage, setErrorMessage] = useState<null | string>(null);

  // Mutation for creating manual version (from listing sources page)
  const { mutateAsync: createManualVersion, isPending: isCreatingManualVersion } =
    useRentalManagerUserFeedListingManagementV2WebApiHandler_createManualVersion({
      meta: {
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserFeedListingManagementV2WebApiHandler_createManualVersion',
        },
        invalidates: ['users-properties-feed-listing-management-create-manual-version'],
      },
    });

  // Mutation for converting feed to manual (from PDP/create listing page)
  const { mutateAsync: convertFeedToManual, isPending: isConverting } =
    useRentalManagerUserPropertyFeedToManualV2WebApiHandler_convert({
      meta: {
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserPropertyFeedToManualV2WebApiHandler_convert',
        },
        invalidates: ['users-properties-feed-to-manual-convert'],
      },
    });

  const handleSubmit = async () => {
    setErrorMessage(null);
    try {
      const { listingAlias: manualListingAlias } = useCreateManualVersion
        ? await createManualVersion({ postData: { listingAlias } })
        : await convertFeedToManual({ postData: { listingAlias } });

      if (manualListingAlias) {
        router.push(
          isMultifamily
            ? getListingRoute({ listingAlias: manualListingAlias })
            : getCreateListingRoute({ listingAlias: manualListingAlias }),
        );
      } else {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
      }
    } catch {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
    }
  };

  const isSubmitting = isCreatingManualVersion || isConverting;

  return (
    <Modal
      multiModalTriggerId="TransferFeedListingModal"
      header={
        <Heading level={1} textStyle="heading-xs">
          Automatically create a new version
        </Heading>
      }
      body={
        <>
          <Icon size="xl" css={{ color: 'icon.accent.blue.hero' }} render={<IconMovingTruckFilled />} />
          <Paragraph css={{ textStyle: 'heading-sm', marginBlock: '400' }}>Transfer your feed listing data</Paragraph>
          <List.Root>
            {infoList.map((item) => (
              <List.Item key={item}>{item}</List.Item>
            ))}
          </List.Root>
        </>
      }
      footer={
        <ModalFooter
          cancelText="Cancel"
          submitText="Create now"
          onSubmit={handleSubmit}
          errorMessage={errorMessage}
          hasError={errorMessage !== null}
          isSubmitting={isSubmitting}
        />
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default TransferFeedListingModal;
